/**
 * WebContainer bootstrapper. Requires the host page to serve:
 *   Cross-Origin-Opener-Policy: same-origin
 *   Cross-Origin-Embedder-Policy: require-corp
 *
 * On the Emergent preview URL these headers are absent, so booting will
 * throw synchronously ("SharedArrayBuffer is not defined"). The rest of
 * OmniCraft transparently falls back to the esbuild-wasm sandbox preview
 * — the two entry points share the same file map contract so no code
 * knows which runtime is active.
 */
let bootPromise = null;
let container = null;

export function canBootWebContainer() {
  return typeof SharedArrayBuffer !== "undefined" && !!globalThis.crossOriginIsolated;
}

export function fileMapToTree(files) {
  const tree = {};
  for (const [rawPath, content] of Object.entries(files)) {
    const parts = rawPath.split("/").filter(Boolean);
    let cursor = tree;
    parts.forEach((part, idx) => {
      const isFile = idx === parts.length - 1;
      if (isFile) {
        cursor[part] = { file: { contents: content } };
      } else {
        cursor[part] = cursor[part] || { directory: {} };
        cursor = cursor[part].directory;
      }
    });
  }
  return tree;
}

export async function bootWebContainer(files, onLog) {
  if (!canBootWebContainer()) {
    throw new Error(
      "WebContainer needs COEP/COOP headers on this origin (SharedArrayBuffer unavailable).",
    );
  }
  if (bootPromise) return bootPromise;
  bootPromise = (async () => {
    const { WebContainer } = await import(
      /* webpackIgnore: true */ "https://unpkg.com/@webcontainer/api@1?module"
    );
    container = await WebContainer.boot();
    await container.mount(fileMapToTree(files));
    const install = await container.spawn("npm", ["install"]);
    install.output.pipeTo(
      new WritableStream({ write: (chunk) => onLog?.(chunk) }),
    );
    const exit = await install.exit;
    if (exit !== 0) throw new Error(`npm install failed (${exit})`);
    const dev = await container.spawn("npm", ["run", "dev"]);
    dev.output.pipeTo(new WritableStream({ write: (chunk) => onLog?.(chunk) }));
    const url = await new Promise((resolve) => {
      container.on("server-ready", (_port, u) => resolve(u));
    });
    return { container, url };
  })();
  return bootPromise;
}

export async function writeContainerFile(path, contents) {
  if (!container) return;
  await container.fs.writeFile(path, contents);
}
